import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { theme } from 'styled-tools'
import PhotoUpload from '../../photo-upload'
import { extractUrlSettings } from '../../../../utils/cloudinary'
import { FormOptions } from '../Options/FormOptions'
import settings from '../../settings'

const OptionsContainer = styled.div`
  display: flex;
  gap: 14px;
  flex-direction: column;
`

const MagnetOptionsContainer = styled.div`
  display: ${theme('options.container.display')};
  flex-direction: ${theme('options.container.flexDirection')};
  justify-content: ${theme('options.container.justifyContent')};
  flex-basis: ${(props) =>
    theme('questionBlock.options.container.flexBasis')(props) || '70%'};
`

const createFile = (url, filename) => {
  if (!url) return null

  const segments = url.split('/')
  if (segments.length < 2) return null

  const { deliveryType, extension, publicId } = extractUrlSettings(url)
  const [container, blobName] = url.split('/').slice(-2)
  const thumbnailUrl = `https://assets.nobiadigital.com/image/${deliveryType}/c_fill,w_240,ar_1/q_auto/f_auto/${publicId}.${extension}`
  return {
    url,
    thumbnailUrl,
    blobName,
    container,
    fileSize: 1,
    fileName: `${filename}.${extension}`,
    fileType: 'IMAGE',
  }
}

export const UploadQuestion = ({
  question,
  answers,
  qrBlockId,
  onChange,
  onLoading,
}) => {
  const [files, setFiles] = useState()
  const [values, setValues] = useState({})

  const options = useMemo(() => {
    const opt = question.options || []
    if (!question.otherAnswerText) {
      return opt
    }
    return [
      ...opt,
      {
        contentType: 'FormFieldOption',
        fieldType: 'checkbox',
        label: question.otherAnswerText,
      },
    ]
  }, [question.options])

  useEffect(() => {
    const f = (answers || [])
      .filter((a) => a && a.startsWith('http'))
      .map((a, i) => createFile(a, `photo-${i}`))
    const v = (answers || [])
      .filter((a) => a && !a.startsWith('http'))
      .map((a) => {
        try {
          if (a.startsWith('{')) {
            return JSON.parse(a)
          } else if (a === question.otherAnswerText) {
            return {
              [question.otherAnswerText]: question.otherAnswerText,
            }
          }
          return undefined
        } catch (error) {
          return undefined
        }
      })
      .filter(Boolean)
      .reduce((a, c) => ({ ...a, ...c }), {})
    setValues(v)
    setFiles(f)
  }, [answers, question.otherAnswerText])

  const update = useCallback(
    (newFiles, newValues) => {
      const simpleValues = Object.keys(newValues).filter(
        (k) => newValues[k] === k
      )
      const form = Object.keys(newValues)
        .filter((k) => !simpleValues.includes(k))
        .reduce((a, c) => ({ ...a, [c]: newValues[c] }), {})

      const updatedFiles = newFiles
        .map((f) => f.url)
        .filter((v, i, a) => a.indexOf(v) === i)
      const updatedValue = [...updatedFiles, ...simpleValues]
      if (Object.keys(form).length) {
        updatedValue.push(JSON.stringify(form))
      }
      const same =
        (answers || []).every((a) => updatedValue.includes(a)) &&
        answers?.length === updatedValue.length
      if (!same) {
        onChange(updatedValue)
      }
    },
    [answers, onChange]
  )

  const onFormBlur = useCallback(() => {
    update(files, values)
  }, [update, files, values])

  const onFormChange = useCallback(
    (newValues, updateDirectly = false) => {
      setValues(newValues)
      if (updateDirectly) {
        update(files, newValues)
      }
    },
    [update, files]
  )

  const onFileChange = useCallback(
    (e) => {
      if (e.pending.length) {
        onLoading()
      } else if (e.completed) {
        update(e.completed, values)
      }
    },
    [update, onLoading, values]
  )

  return settings.brand === 'magnet' ? (
    <MagnetOptionsContainer>
      <PhotoUpload
        uploadedFiles={files}
        onChange={onFileChange}
        fileInputId={`photoUpload${question.sortOrder}`}
        qrBlockId={qrBlockId}
      />
      <FormOptions
        options={options}
        values={values}
        onChange={onFormChange}
        onBlur={onFormBlur}
      />
    </MagnetOptionsContainer>
  ) : (
    <OptionsContainer>
      <PhotoUpload
        uploadedFiles={files}
        onChange={onFileChange}
        fileInputId={`photoUpload${question.sortOrder}`}
        qrBlockId={qrBlockId}
      />
      <FormOptions
        options={options}
        values={values}
        onChange={onFormChange}
        onBlur={onFormBlur}
      />
    </OptionsContainer>
  )
}
